<template>
  <div v-if="loaded">
    <div class="header blur">
      <div
        v-if="track.cover_medium"
        :style="{ backgroundImage: 'url(' + track.cover_medium + ')' }"
        class="bg-image"
      />
      <div
        v-if="!track.cover_medium"
        class="bg-image"
      />
    </div>
    <div class="container">
      <div class="gutter">
        <div class="columns">
          <div class="column is-narrow sidebar is-hidden-touch">
            <section class="track-image cover-large">
              <figure class="image is-square">
                <img
                  v-if="track.cover_medium"
                  :src="track.cover_medium"
                >
                <img
                  v-if="!track.cover_medium"
                  src="@/assets/artist-default.png"
                >
              </figure>
            </section>
            <section class="details">
              <h4 class="title is-4">
                Details
              </h4>
              <div
                v-if="track.genres.length > 0"
                class="tags columns"
              >
                <div class="column is-narrow">
                  <span class="text label-small color-bluegrey1">Genre</span>
                </div>
                <div class="column">
                  <span
                    v-for="genre in track.genres"
                    :key="genre"
                    class="tag is-rounded light"
                  >{{ genre }}</span>
                </div>
              </div>
              <div
                v-if="track.moods.length > 0"
                class="tags columns"
              >
                <div class="column is-narrow">
                  <span class="text label-small color-bluegrey1">Mood</span>
                </div>
                <div class="column">
                  <span
                    v-for="mood in track.moods"
                    :key="mood"
                    class="tag is-rounded light"
                  >{{ mood }}</span>
                </div>
              </div>
              <div
                v-if="track.themes.length > 0"
                class="tags columns"
              >
                <div class="column is-narrow">
                  <span class="text label-small color-bluegrey1">Theme</span>
                </div>
                <div class="column">
                  <span
                    v-for="theme in track.themes"
                    :key="theme"
                    class="tag is-rounded light"
                  >{{ theme }}</span>
                </div>
              </div>
              <div
                v-if="track.bpm"
                class="tags columns"
              >
                <div class="column is-narrow">
                  <span class="text label-small color-bluegrey1">BPM</span>
                </div>
                <div class="column">
                  <span class="tag is-rounded light">{{ track.bpm }}</span>
                </div>
              </div>
            </section>
            <section
              v-if="track.artist.biography"
              class="bio"
            >
              <h4 class="title is-4">
                Bio
              </h4>
              <p>{{ track.artist.biography }}</p>
            </section>
          </div>
          <div
            v-if="track"
            class="column main"
            :class="{'has-featured-artist': track.featuring_title}"
          >
            <h2 class="subtitle is-marginless text color-red">
              Track
            </h2>
            <h1 class="title is-1 text color-white">
              {{ track.display_title }}
            </h1>
            <h4 class="title is-4 text color-white">
              {{ track.featuring_title }}
            </h4>
            <h3 class="title is-3 text color-white">
              <router-link :to="artistRouteTo">
                {{ track.artist.name }}
              </router-link>
            </h3>
            <div class="box is-large">
              <div class="columns track-detail is-mobile">
                <div class="column is-narrow">
                  <a
                    class="play"
                    :class="{ 'is-pause': isTrackPlaying }"
                    @click="togglePlay"
                  />
                </div>
                <div class="column">
                  <div
                    :id="`waveform-${id}`"
                    class="waveform margin-bottom-xl"
                  />
                </div>
                <div class="column is-narrow">
                  <div class="buttons are-small track-actions">
                    <b-tooltip
                      label="Add to collection"
                      type="is-dark"
                      position="is-top"
                    >
                      <a
                        class="icon-button plus-muted"
                        :disabled="!isLoggedIn"
                        @click="handleAddToCollection"
                      />
                    </b-tooltip>
                    <b-tooltip
                      label="Remove from favourites"
                      type="is-dark"
                      position="is-top"
                    >
                      <a
                        v-show="isFavourite"
                        class="icon-button heart-active"
                        @click="handleFavouriteDelete"
                      />
                    </b-tooltip>
                    <b-tooltip
                      label="Add to favourites"
                      type="is-dark"
                      position="is-top"
                    >
                      <a
                        v-show="!isFavourite"
                        class="icon-button heart-muted"
                        @click="handleFavouriteCreate"
                      />
                    </b-tooltip>
                    <b-tooltip
                      label="Download preview"
                      type="is-dark"
                      position="is-top"
                    >
                      <a
                        class="icon-button preview-muted"
                        @click="handleShowPreviewModal"
                      />
                    </b-tooltip>
                    <b-tooltip
                      label="License"
                      type="is-dark"
                      position="is-top"
                    >
                      <b-dropdown
                        v-if="isLoggedIn"
                        class="is-bottom-left"
                        aria-role="list"
                      >
                        <a
                          slot="trigger"
                          class="icon-button download"
                        />

                        <b-dropdown-item
                          aria-role="listitem"
                          @click="handleAddToCart"
                        >
                          <i class="icon-button download">
                            <span style="font-style: normal; padding-left: 32px;">Add to cart</span>
                          </i>
                        </b-dropdown-item>

                        <b-dropdown-item
                          aria-role="listitem"
                          @click="handleShowSubscriptionDownload"
                        >
                          <i class="icon-button download">
                            <span style="font-style: normal; padding-left: 32px;">Subscription</span>
                          </i>
                        </b-dropdown-item>
                      </b-dropdown>
                      <a
                        v-if="!isLoggedIn"
                        class="icon-button download"
                        @click="$store.dispatch('common/startSignupWorkflow', { reason: 'download', track })"
                      />
                    </b-tooltip>
                  </div>
                </div>
              </div>
              <div
                v-if="instrumentalTrack"
                class="margin-bottom-xl"
              >
                <h3 class="title is-3">
                  Instrumental
                </h3>

                <track-list-item
                  :key="instrumentalTrack.pk"
                  :track="instrumentalTrack"
                />
              </div>

              <div v-if="similarTracks.length > 0">
                <h3 class="title is-3">
                  Similar songs
                </h3>

                <track-list-item
                  v-for="similarTrack in similarTracks"
                  :key="similarTrack.pk"
                  :track="similarTrack"
                  :show-waveform="true"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import WaveSurfer from 'wavesurfer.js'
import favouritesClient from '@/api/favouriteTracks.js'
import searchClient from '@/api/trackSearch.js'
import trackClient from '@/api/tracks.js'
import TrackListItem from '@/components/TrackListItem.vue'
import { formatComponentName } from '@/utils/common.js'
import { logPlayTrack } from '@/utils/segment.js'
import { addTrackToCart, showSubscriptionDownload, showPreviewDownload } from '@/utils/trackLicensing.js'

export default {
  name: 'TrackDetail',

  components: {
    TrackListItem
  },

  props: {
    trackId: {
      type: String,
      required: true
    }
  },

  data () {
    return {
      id: null,
      wavesurfer: null,
      loaded: false,
      track: null,
      instrumentalTrack: null,
      similarTracks: [],
      tags: [],
      creatingFavourite: false,
      deletingFavourite: false
    }
  },

  computed: {
    // How far through the player is, while this page's track is the one loaded (playing
    // or paused); 0 otherwise.
    playProgress: function () {
      const current = this.$store.getters['player/currentTrack']
      if (current && this.track && current.pk === this.track.pk) {
        return this.$store.getters['player/playProgress']
      }
      return 0
    },

    isTrackPlaying: function () {
      const current = this.$store.getters['player/currentTrack']
      return Boolean(current && this.track && current.pk === this.track.pk && this.$store.getters['player/isPlaying'])
    },

    isLoggedIn: function () {
      return this.$store.getters['auth/isLoggedIn']
    },

    artistRouteTo: function () {
      return {
        name: 'artist-detail',
        params: {
          slug: this.track.artist.slug,
          artistId: this.track.artist.pk
        }
      }
    },

    isFavourite: function () {
      if (this.isLoggedIn) {
        if (this.deletingFavourite) {
          return false
        }
        return this.creatingFavourite || this.$store.getters['favouriteTracks/isFavourite'](this.track.pk)
      }
      return false
    },

    artistTrackLabel: function () {
      if (this.track) {
        return `${this.track.artist.name} - ${this.track.display_title}`
      }
      return ''
    },

    componentName: function () {
      return formatComponentName(this)
    }
  },

  watch: {
    playProgress () {
      this.drawPlayProgress()
    }
  },

  created () {
    this.id = this._uid
  },

  mounted () {
    const fetchCalls = []

    fetchCalls.push(
      // Grab release from API.
      trackClient.retrieve(this.trackId)
        .then(response => {
          this.track = response.data
          if (this.track.status !== 'visible') {
            this.$router.push({ name: 'not-found' })
          }

          // Returned so the Promise.all below waits for it: otherwise the play queue can be
          // built before it arrives and its row's play button does nothing.
          const instrumentalFetch = this.track.instrumental_version
            ? trackClient.retrieve(this.track.instrumental_version)
              .then(response => {
                this.instrumentalTrack = response.data
              })
              .catch(() => {})
            : null

          // Load search options so we can get tag field names for display
          // This call required the `track` hence why it's embedded here.
          searchClient.searchOptionsTrack().then(optionsResponse => {
            const tags = []
            let classIndex = 0
            const classes = [
              'is-dark',
              'is-primary',
              'is-link',
              'is-info',
              'is-success',
              'is-warning',
              'is-danger'
            ]

            Object.keys(optionsResponse.data).forEach(key => {
              const value = this.track[key]

              if (Array.isArray(value)) {
                if (value.length === 0) {
                  return
                }
                for (const x of value) {
                  tags.push({ name: x, field: key, class: classes[classIndex] })
                }
              } else if (value !== null && typeof value !== 'undefined') {
                tags.push({ name: value, field: key, class: classes[classIndex] })
              }

              classIndex++
              classIndex = classIndex % classes.length
            })

            this.tags = tags
          })

          return instrumentalFetch
        })
        .catch(error => {
          if (error.response.status === 404) {
            this.$router.push({ name: 'not-found' })
          }
        })
    )

    fetchCalls.push(
      trackClient.similar(this.trackId)
        .then(response => {
          this.similarTracks = response.data
        })
        .catch(() => {})
    )

    // Fetch data then load the audio.
    const vm = this
    Promise.all(fetchCalls).then(function () {
      // Left the page before the data arrived: don't create a waveform for it or replace
      // the play queue of the page the user is now on.
      if (vm._isDestroyed) {
        return
      }
      vm.loaded = true
      // Load audio after next DOM update otherwise we can't access the
      // (until then) hidden waveform div.
      vm.$nextTick(() => {
        vm.loadAudio()
      })
    })
  },

  // The router-view is keyed by path, so every track page visit is a new instance. Without
  // this each one left its WaveSurfer (and its window resize listener) behind.
  destroyed () {
    if (this.wavesurfer !== null) {
      this.wavesurfer.destroy()
    }
  },

  methods: {
    loadAudio: function () {
      if (this.wavesurfer === null) {
        const waveSelector = `#waveform-${this.id}`
        const waveformData =
          this.track.waveform_data || this.track.waveform.data
        const audioUri = this.track.streaming_m4a

        this.wavesurfer = WaveSurfer.create({
          container: waveSelector,
          waveColor: '#D0D2D6',
          progressColor: '#FF6C6C', // Same as the player's waveform.
          cursorColor: '#fff',
          barWidth: 2,
          height: 44,
          interact: false,
          responsive: true
        })
        // Drawing (on load and on resize) resets progress to this instance's own position,
        // which is always 0 as it never plays, so reapply the player's progress.
        this.wavesurfer.on('redraw', this.drawPlayProgress)

        this.wavesurfer.load(audioUri, waveformData)
      }

      const tracks = [this.track]
      if (this.instrumentalTrack) {
        tracks.push(this.instrumentalTrack)
      }
      if (this.similarTracks.length > 0) {
        tracks.push(...this.similarTracks)
      }
      if (tracks.length > 0) {
        this.$store.dispatch('player/loadTracks', tracks)
      }
    },

    handleAddToCollection () {
      if (this.isLoggedIn) {
        this.$store.dispatch(
          'collections/setTrackAddingToCollection',
          this.track
        )
        this.$store.dispatch('collections/showCollectionsModal')
      } else {
        this.$store.dispatch('user/showSignupModal', { reason: 'collection', track: this.track })
      }
    },

    handleFavouriteCreate () {
      if (this.isLoggedIn) {
        this.creatingFavourite = true
        favouritesClient
          .create({ item_pk: this.track.pk })
          .then(response => {
            this.$store.dispatch('favouriteTracks/appendToFavouriteTracks', response.data)
              .then(() => {
                this.creatingFavourite = false
              })
          })
          .catch(() => {
            this.creatingFavourite = false
          })
      } else {
        this.$store.dispatch('user/showSignupModal', { reason: 'favourite', track: this.track })
      }
    },

    handleFavouriteDelete () {
      this.deletingFavourite = true

      const favePk = this.$store.getters['favouriteTracks/getFavouritePkFromTrackPk'](this.track.pk)
      favouritesClient.delete(favePk)
        .then(response => {
          this.$store.dispatch('favouriteTracks/removeFromFavouriteTracks', favePk)
            .then(() => {
              this.deletingFavourite = false
            })
        })
        .catch(() => {
          this.deletingFavourite = false
        })
    },

    handleAddToCart () {
      return addTrackToCart(this, this.track)
    },

    handleShowSubscriptionDownload () {
      showSubscriptionDownload(this, this.track)
    },

    handleShowPreviewModal () {
      showPreviewDownload(this, this.track)
    },

    drawPlayProgress () {
      // Paint straight onto the drawer: seekTo() would count as an interaction and make
      // this peaks-only waveform download the audio.
      if (this.wavesurfer !== null && this.wavesurfer.drawer) {
        this.wavesurfer.drawer.progress(this.playProgress)
      }
    },

    togglePlay () {
      if (this.isTrackPlaying) {
        this.$store.dispatch('player/pause')
        return
      }
      if (this.$store.getters['player/isPlaybackBlocked']) {
        this.$store.dispatch('common/startSignupWorkflow', { reason: 'play', track: this.track })
        return
      }
      if (this.$store.getters['player/isPlaybackSoftBlocked']) {
        this.$store.dispatch('common/startSignupWorkflow', { reason: 'play', track: this.track })
      }
      this.$store.dispatch('player/playTrack', this.trackId)
      logPlayTrack(this.track.artist.name, this.track.display_title)
    }
  },

  metaInfo () {
    if (this.track) {
      let url = window.location.origin
      const path = this.$route.path
      if (path !== '/') {
        url += path
      }
      const parts = [`A ${this.track.moods[0]} ${this.track.genres[0]} track from ${this.track.artist.name}`]
      if (this.track.themes.length > 0) {
        parts.push(`, perfect for ${this.track.themes[0]} vibes`)
      }
      parts.push('. License now.')
      const description = parts.join('')
      const title = `${this.track.display_title} by ${this.track.artist.name}`

      return {
        title,
        meta: [
          { name: 'description', content: description },
          { property: 'og:url', content: url },
          { property: 'og:title', content: title },
          { property: 'og:description', content: description },
          { property: 'og:image', content: this.track.cover_thumbnail || '/img/sonata-meta.png' },
          { name: 'twitter:card', content: 'website' }
        ]
      }
    } else {
      return {
        title: 'Music Licensing'
      }
    }
  }
}
</script>

<style lang="scss">
.column.main {
  margin-top: 64px;
}

.column.main.has-featured-artist {
  margin-top: 32px;
}

.details {
  clear: both;
  overflow: hidden;
  margin-bottom: 24px;
}
.waveform {
  margin-bottom: 40px;
}

.column.is-narrow.sidebar {
  width: 300px;
  margin-right: 24px;
  margin-top: 32px;
  section {
    margin-bottom: 32px;
    .track-image {
      min-height: 288px;
      img {
        width: 288px;
        height: 288px;
      }
    }
  }
}

.column .track-actions {
  padding-top: 10px
}
</style>
